// @flow
import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import makeStyles from "@material-ui/core/styles/makeStyles";
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import type { Config, Zone, ShapeType } from '../../../types';
import mapService from '../../../services/map';
import ZoneListItemContainer from './containers/ZoneListItemContainer';
import ZoneListControls from './ZoneListControls';

const useStyles = makeStyles(theme => ({
    empty: {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.hint,
        textAlign: 'center'
    }
}));

type Props = $ReadOnly<{
    zonesConfig: $PropertyType<Config, 'zones'>,
    list: $ReadOnlyArray<$PropertyType<Zone, 'id'>>,
    locatePadding?: [number, number, number, number],
    drawingZone?: Zone,

    enableDrawing: (type?: ShapeType, zoneId?: string) => void,
    activateZones: (zoneIds: $ReadOnlyArray<string>, duration?: number) => void,
    deactivateZones: (zoneIds: $ReadOnlyArray<string>) => void,
    deleteZone: (zoneId: string) => void
}>;

function ZoneList({ zonesConfig, list, locatePadding, drawingZone, enableDrawing, activateZones, deactivateZones, deleteZone }: Props) {
    const classes = useStyles();
    const [selected, setSelected] = useState([]);
    const hasMap = mapService.hasMap();

    useEffect(() => {
        if(hasMap) {
            mapService.selectZones(selected);
        }
    }, [hasMap, selected]);

    if(!list || list.length === 0) {
        return (
            <Box my={2} p={2} className={classes.empty}>
                <FormattedMessage
                    id="zoneList.empty"
                    defaultMessage="No zones found"
                />
            </Box>
        );
    }

    const onClick = (zone) => {
        if(!drawingZone || drawingZone.id !== zone.id) {
            enableDrawing(zone.feature.type, zone.feature, zone);
        }
    };

    const onLocateClick = (zone) => {
        mapService.locateZone(zone, {
            padding: locatePadding
        });
    };

    const onSelect = (id, isSelected) => {
        const index = selected.indexOf(id);
        if(isSelected) {
            if(index === -1) {
                setSelected([
                    ...selected,
                    id
                ]);
            }
        }
        else {
            const newSelected = selected.slice();
            newSelected.splice(index, 1);
            setSelected(newSelected);
        }
    };

    const handleActivate = (duration) => {
        if(selected.length > 0) {
            if(duration === 0) {
                deactivateZones(selected);
            }
            else {
                activateZones(selected, duration || undefined);
            }
        }
    };

    const onDelete = (id) => {
        deleteZone(id);
    };

    return (
        <div>
            <ZoneListControls disabled={selected.length === 0} durations={zonesConfig.durations} onActivate={handleActivate} />

            <Table size="small">
                <TableBody>
                    {list && list.length !== 0 &&
                    list.map((zoneId) => (
                        <ZoneListItemContainer
                            key={zoneId}
                            id={zoneId}
                            selected={selected.indexOf(zoneId) !== -1}
                            onClick={onClick}
                            onLocateClick={onLocateClick}
                            onSelect={onSelect}
                            onDelete={onDelete}
                        />
                    ))
                    }
                </TableBody>
            </Table>
        </div>
    );
}

export default ZoneList;